import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { VisitNotesResponse } from "./types";

interface NotesPanelProps {
  visitIdValue: number | null;
  patientIdValue: string;
  showToast: (_args: { message: string; type: "success" | "error" | "info" | "warning" }) => void;
}

export default function NotesPanel({ visitIdValue, patientIdValue, showToast }: NotesPanelProps) {
  const [clinicalNote, setClinicalNote] = useState("");
  const [noteHabits, setNoteHabits] = useState("");
  const [noteObservation, setNoteObservation] = useState("");
  const [loadingNote, setLoadingNote] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const noteSavedSnapshotRef = useRef({ complaints: "", habits: "", observation: "" });
  const noteSaveTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!visitIdValue) {
      setClinicalNote("");
      setNoteHabits("");
      setNoteObservation("");
      noteSavedSnapshotRef.current = { complaints: "", habits: "", observation: "" };
      return;
    }
    let mounted = true;
    const loadNote = async () => {
      setLoadingNote(true);
      try {
        const res = (await apiGet(`/api/doctor/visit-notes/${visitIdValue}`)) as VisitNotesResponse;
        if (!mounted) return;
        const nextNote = String(res?.complaints || res?.notes || "");
        const nextHabits = String(res?.habits || "");
        const nextObservation = String(res?.observation || "");
        setClinicalNote(nextNote);
        setNoteHabits(nextHabits);
        setNoteObservation(nextObservation);
        noteSavedSnapshotRef.current = {
          complaints: nextNote,
          habits: nextHabits,
          observation: nextObservation,
        };
      } catch {
        if (!mounted) return;
        setClinicalNote("");
        setNoteHabits("");
        setNoteObservation("");
        noteSavedSnapshotRef.current = { complaints: "", habits: "", observation: "" };
      } finally {
        if (mounted) setLoadingNote(false);
      }
    };
    void loadNote();
    return () => {
      mounted = false;
    };
  }, [visitIdValue]);

  useEffect(() => {
    if (!visitIdValue || !patientIdValue) return;
    const prev = noteSavedSnapshotRef.current;
    const unchanged =
      prev.complaints === clinicalNote &&
      prev.habits === noteHabits &&
      prev.observation === noteObservation;
    if (unchanged) return;
    if (noteSaveTimerRef.current) window.clearTimeout(noteSaveTimerRef.current);

    noteSaveTimerRef.current = window.setTimeout(async () => {
      setSavingNote(true);
      try {
        await apiPost("/api/doctor/visit-notes", {
          visit_id: visitIdValue,
          patient_id: patientIdValue,
          complaints: clinicalNote,
          habits: noteHabits,
          observation: noteObservation,
        });
        noteSavedSnapshotRef.current = {
          complaints: clinicalNote,
          habits: noteHabits,
          observation: noteObservation,
        };
      } catch {
        showToast({ message: "Failed to save notes.", type: "error" });
      } finally {
        setSavingNote(false);
      }
    }, 550);

    return () => {
      if (noteSaveTimerRef.current) window.clearTimeout(noteSaveTimerRef.current);
    };
  }, [clinicalNote, noteHabits, noteObservation, visitIdValue, patientIdValue, showToast]);

  const hasUnsavedNotes =
    noteSavedSnapshotRef.current.complaints !== clinicalNote ||
    noteSavedSnapshotRef.current.habits !== noteHabits ||
    noteSavedSnapshotRef.current.observation !== noteObservation;

  if (!visitIdValue) {
    return (
      <div className="mod-standalone-window">
        <div className="mod-empty-state" style={{ height: "260px", border: "none" }}>
          <div className="mod-empty-icon-wrapper" style={{ background: "#e0f2fe", color: "#0ea5e9" }}>
            <span className="mod-empty-icon" style={{ opacity: 1 }}>Notes</span>
          </div>
          <h2>No Active Visit</h2>
          <p>Select a patient to add clinical notes.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mod-standalone-window" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <div className="mod-window-header" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
        <div>
          <h2 style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            Clinical Notes
          </h2>
          <p className="mod-text-muted">Record complaints, observations, and relevant habits.</p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "0.8rem",
            fontWeight: 700,
            color: hasUnsavedNotes || savingNote ? "#d97706" : "#10b981",
            background: hasUnsavedNotes || savingNote ? "#fef3c7" : "#d1fae5",
            padding: "4px 10px",
            borderRadius: "20px",
          }}
        >
          {loadingNote ? "Loading..." : savingNote ? "Saving..." : hasUnsavedNotes ? "Unsaved" : "Saved"}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "14px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700 }}>Chief Complaint</label>
          <textarea
            value={clinicalNote}
            onChange={(e) => setClinicalNote(e.target.value)}
            placeholder="Patient's primary reason for visit..."
            style={{ width: "100%", minHeight: "110px", padding: "10px", borderRadius: "10px", border: "1px solid #cbd5e1", background: "#f8fafc", resize: "vertical", fontSize: "0.9rem", fontFamily: "inherit" }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700 }}>Clinical Observations</label>
          <textarea
            value={noteObservation}
            onChange={(e) => setNoteObservation(e.target.value)}
            placeholder="Doctor's findings..."
            style={{ width: "100%", minHeight: "110px", padding: "10px", borderRadius: "10px", border: "1px solid #cbd5e1", background: "#f8fafc", resize: "vertical", fontSize: "0.9rem", fontFamily: "inherit" }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700 }}>Medical / Dental Habits</label>
          <textarea
            value={noteHabits}
            onChange={(e) => setNoteHabits(e.target.value)}
            placeholder="Relevant history or habits..."
            style={{ width: "100%", minHeight: "110px", padding: "10px", borderRadius: "10px", border: "1px solid #cbd5e1", background: "#f8fafc", resize: "vertical", fontSize: "0.9rem", fontFamily: "inherit" }}
          />
        </div>
      </div>
    </div>
  );
}
